import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Tag } from 'antd';
import toast from 'react-hot-toast';
import { Megaphone, Plus, RefreshCw } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminDesignSystem, ADMIN_ROOT, AdminThemeProvider, getAdminTokens } from './adminDesignSystem';
import AdminSidebar from './AdminSidebar';

const API = '/api/v1/announcements';

const AdminAnnouncements = () => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const designSystem = useMemo(() => getAdminDesignSystem(isDark), [isDark]);
  const tk = useMemo(() => getAdminTokens(isDark), [isDark]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API);
      setRows(res.data?.data?.announcements || []);
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Could not load announcements');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const post = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(API, { title: title.trim(), body: body.trim() });
      setTitle('');
      setBody('');
      toast.success('Posted — orgs see it after refresh');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Could not post');
    } finally {
      setSaving(false);
    }
  };

  const setPublished = async (id, published) => {
    try {
      await api.patch(`${API}/${id}`, { published });
      toast.success(published ? 'Live again' : 'Hidden');
      fetchAll();
    } catch (_e) {
      toast.error('Could not update');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`${API}/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (_e) {
      toast.error('Could not delete');
    }
  };

  const card = {
    background: tk.panel,
    border: `1px solid ${tk.border}`,
    borderRadius: 16,
    padding: 16,
  };
  const latest = rows.find((r) => r.published) || null;

  return (
    <>
      <style>{designSystem}</style>
      <AdminThemeProvider>
        <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden`}>
          <AdminSidebar activeKey="announcements" />
          <div style={{ flex: 1, overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <h1 style={{ margin: 0, fontSize: 22, color: tk.text, fontWeight: 650 }}>Announcements</h1>
                <p style={{ margin: '6px 0 0', color: tk.text2, fontSize: 13, maxWidth: 560 }}>
                  Posts to every org dashboard. Not instant — they see it on refresh.
                  If an org admin closes one, that id stays closed. A new post shows again.
                </p>
              </div>
              <Button icon={<RefreshCw size={14} />} onClick={fetchAll} loading={loading}>Refresh</Button>
            </div>

            <div style={{
              ...card,
              marginBottom: 18,
              background: isDark
                ? 'linear-gradient(135deg, rgba(0,209,193,.10), transparent 55%)'
                : 'linear-gradient(135deg, #e8f7f4, #fff 60%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: tk.accentTx, fontWeight: 700 }}>
                <Megaphone size={16} /> New announcement
              </div>
              <Input
                placeholder="Short title — what changed"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={140}
                style={{ marginBottom: 10 }}
              />
              <Input.TextArea
                placeholder="The note orgs will read on their dashboard"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={2000}
                autoSize={{ minRows: 3, maxRows: 8 }}
                style={{ marginBottom: 12 }}
              />
              <Button type="primary" icon={<Plus size={14} />} onClick={post} loading={saving}>
                Post for every org
              </Button>
            </div>

            {latest ? (
              <div style={{ ...card, marginBottom: 16, borderColor: tk.accent }}>
                <div style={{ fontSize: 11, fontWeight: 750, letterSpacing: '0.08em', textTransform: 'uppercase', color: tk.accentTx, marginBottom: 6 }}>
                  Live now
                </div>
                <div style={{ fontWeight: 700, color: tk.text, fontSize: 16 }}>{latest.title}</div>
                <div style={{ color: tk.text2, fontSize: 13.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{latest.body}</div>
              </div>
            ) : null}

            {loading ? (
              <div style={{ color: tk.text3 }}>Loading…</div>
            ) : rows.length === 0 ? (
              <Empty description="No announcements yet" />
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {rows.map((r) => (
                  <div key={r.id} style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 650, color: tk.text }}>{r.title}</div>
                        <div style={{ fontSize: 12, color: tk.text2, marginTop: 4 }}>
                          {r.createdByName || 'Admin'} · {new Date(r.createdAt).toLocaleString()}
                        </div>
                        <div style={{ fontSize: 13.5, color: tk.text, marginTop: 8, whiteSpace: 'pre-wrap' }}>{r.body}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                        <Tag color={r.published ? 'green' : 'default'}>{r.published ? 'Published' : 'Hidden'}</Tag>
                        <Button size="small" onClick={() => setPublished(r.id, !r.published)}>
                          {r.published ? 'Hide' : 'Publish'}
                        </Button>
                        <Button size="small" danger onClick={() => remove(r.id)}>Delete</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </AdminThemeProvider>
    </>
  );
};

export default AdminAnnouncements;
